import { observer } from 'mobx-react'
import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, InlineAlert } from 'suomifi-ui-components'
import { TodistusStatus } from 'todistukset-common/src/vtj/types/todistus-status'
import { isHetuIdentifiedUser } from 'todistukset-common/src/vtj/user.utils'
import { finalEditableFieldValue } from 'todistukset-common/src/vtj/types/editable-field.type'
import {
  HiddenSpan,
  StyledBlock,
  StyledParagraph,
} from 'todistukset-ui/src/vtj/components/aet-todistuspyynto-banner/AetTodistuspyyntoBanner.sc'
import LocalizedString from 'todistukset-ui/src/vtj/components/localized-string/LocalizedString.react'
import { useNavigateToAetSignerForm } from 'todistukset-ui/src/vtj/components/order-wizard-aet-signer/aetSignerSteps'
import { useStore } from 'todistukset-ui/src/vtj/stores/store.util'
import { TodistusPyyntöAet } from 'todistukset-common/src/vtj/types/todistus'
import { Serialized } from 'common/src/vtj/types/util.type'

const AetTodistuspyyntoBanner = observer((): React.JSX.Element => {
  const navigateToAetSignerForm = useNavigateToAetSignerForm()
  const { t } = useTranslation()

  const store = useStore()
  const {
    common: { lang, user },
    aetStore: { aetOrders },
  } = store

  if (!user || !isHetuIdentifiedUser(user)) {
    return <></>
  }

  const { loadTodistuspyynto, isLoading, error, aetTodistuspyynto } =
    store.aetTodistuspyyntoBannerStore

  const unApprovedAetTodistuspyynnot = aetOrders.filter(
    ({ status, aetLisaosa }) =>
      status === TodistusStatus.LUONNOS && aetLisaosa.spouseBHetu === user.hetu
  )

  const { requestId: todistuspyyntoId } = unApprovedAetTodistuspyynnot[0] || {}

  useEffect(() => {
    if (todistuspyyntoId && !aetTodistuspyynto && !isLoading && !error) {
      loadTodistuspyynto(todistuspyyntoId)
    }
  }, [
    loadTodistuspyynto,
    aetTodistuspyynto,
    isLoading,
    error,
    todistuspyyntoId,
  ])

  if (!todistuspyyntoId) {
    return <></>
  }

  const buttonIsLoading = !aetTodistuspyynto || isLoading

  return (
    <StyledBlock>
      <InlineAlert
        status="neutral"
        labelText={t('aetSeeTodistuspyyntoBannerLabelText')}
        data-test-id={`aet-todistuspyynto-banner-for-${todistuspyyntoId}`}
      >
        <AlertContentText {...{ aetTodistuspyynto }} />
        <StyledParagraph>
          <Button
            {...(buttonIsLoading
              ? { loading: true, ariaLoadingText: t('aetLoadingOrder') }
              : { loading: false })}
            data-test-id="aet-todistuspyynto-banner-see-todistuspyynto-button"
            variant="default"
            onClick={() => navigateToAetSignerForm(todistuspyyntoId, lang)}
          >
            <LocalizedString stringKey="aetSeeTodistuspyyntoButtonText" />
          </Button>
        </StyledParagraph>
      </InlineAlert>
    </StyledBlock>
  )
})

export default AetTodistuspyyntoBanner

const AlertContentText: React.FC<{
  aetTodistuspyynto: Serialized<TodistusPyyntöAet> | null
}> = ({ aetTodistuspyynto }) =>
  aetTodistuspyynto ? (
    <LocalizedAetPyyntoText {...{ aetTodistuspyynto }} />
  ) : (
    <>
      <LocalizedString stringKey="aetLoadingOrder" />
      {/* This tries to prevent cumulative layout shift on page when loading aetTodistuspyynto finishes */}
      <HiddenSpan>
        <LocalizedAetPyyntoText {...{ aetTodistuspyynto }} />
      </HiddenSpan>
    </>
  )

const LocalizedAetPyyntoText: React.FC<{
  aetTodistuspyynto: Serialized<TodistusPyyntöAet> | null
}> = ({ aetTodistuspyynto }) => (
  <LocalizedString
    stringKey="aetSeeTodistuspyyntoBannerContentText"
    values={{
      ordererFullName: aetTodistuspyynto
        ? `${finalEditableFieldValue(
            aetTodistuspyynto.orderer.firstNames
          )} ${finalEditableFieldValue(aetTodistuspyynto.orderer.lastName)}`
        : ' ...',
    }}
  />
)
